import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Button } from '@common/components/button';
import Alert from '@common/components/alert';
import { TextInput } from '@common/components/form/inputs/text/text-input';
import { Checkbox } from '@common/components/form/inputs/checkbox/checkbox-input';
import { test as validationTest } from '@utils/validation';
import { isWorkEmail } from './domain';

export type DetailsErrorKind = 'existing' | 'token' | 'rate_limited' | 'failed';

type Props = {
  companyName: string;
  initialEmail: string;
  error: DetailsErrorKind | null;
  isSubmitting: boolean;
  onSubmit: (values: { firstName: string; email: string; password: string }) => void;
  onBack: () => void;
};

export const DetailsStep = ({
  companyName, initialEmail, error, isSubmitting, onSubmit, onBack,
}: Props) => {
  const { t } = useTranslation();
  const [firstName, setFirstName] = React.useState('');
  const [email, setEmail] = React.useState(initialEmail);
  const [password, setPassword] = React.useState('');
  const [consent, setConsent] = React.useState(false);
  const [emailTouched, setEmailTouched] = React.useState(false);
  const [passwordTouched, setPasswordTouched] = React.useState(false);

  const emailValid = isWorkEmail(email);
  const passwordValid = password.length > 0 && validationTest('PASSWORD', password);
  const canSubmit = firstName.trim().length > 0 && emailValid && passwordValid && consent;

  return (
    <form
      className="Form"
      onSubmit={(e) => {
        e.preventDefault();
        if (canSubmit) {
          onSubmit({
            firstName: firstName.trim(),
            email: email.trim(),
            password,
          });
        }
      }}
    >
      <h2>{t('authentication:trial_details_title')}</h2>
      <div className="Authentication__Form__Description">
        {t('authentication:trial_details_description', { name: companyName })}
      </div>

      {error === 'existing' && (
        <Alert type="warning">
          <Trans i18nKey="authentication:trial_details_error_existing" components={[<a href="/auth/login" />]} />
        </Alert>
      )}
      {error === 'token' && <Alert type="warning">{t('authentication:trial_details_error_token')}</Alert>}
      {error === 'rate_limited' && <Alert type="warning">{t('authentication:trial_details_error_rate_limited')}</Alert>}
      {error === 'failed' && (
        <Alert type="warning" title={t('authentication:error_creating_trial')}>
          <Trans
            i18nKey="authentication:error_creating_trial_description"
            components={[<a onClick={() => window.Intercom && window.Intercom('show')} role="link" />]}
          />
        </Alert>
      )}

      <div className="Form__Row">
        <div className="Form__Group">
          <label className="Form__Label" htmlFor="trial-first-name">{t('authentication:trial_details_first_name')}</label>
          <TextInput
            id="trial-first-name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            autoComplete="given-name"
            autoFocus
          />
        </div>
      </div>
      <div className="Form__Row">
        <div className="Form__Group">
          <label className="Form__Label" htmlFor="trial-email">{t('authentication:trial_details_email')}</label>
          <TextInput
            id="trial-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onBlur={() => setEmailTouched(true)}
            autoComplete="email"
            error={emailTouched && !emailValid}
          />
          <div className="FreeTrial__Hint">{t('authentication:trial_details_email_hint')}</div>
        </div>
      </div>
      <div className="Form__Row">
        <div className="Form__Group">
          <label className="Form__Label" htmlFor="trial-password">{t('authentication:trial_details_password')}</label>
          <TextInput
            id="trial-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onBlur={() => setPasswordTouched(true)}
            autoComplete="new-password"
            error={
              passwordTouched && !passwordValid
                ? (t('authentication:password_requirements') as string)
                : undefined
            }
          />
        </div>
      </div>
      <div className="Form__Row">
        <Checkbox
          value={consent}
          onChange={setConsent}
          label={(
            <Trans
              i18nKey="authentication:trial_details_consent"
              components={[<a href={t('authentication:consent_url')} target="_blank" rel="noreferrer" />]}
            />
          )}
        />
      </div>
      <Button
        type="primary"
        size="fill"
        buttonType="submit"
        disabled={!canSubmit}
        isLoading={isSubmitting}
        iconRight="arrow_forward"
      >
        {t('authentication:trial_details_submit', { name: companyName })}
      </Button>
      <div className="Authentication__Form__Action">
        <a onClick={onBack} role="link">{t('authentication:trial_details_back')}</a>
      </div>
    </form>
  );
};
